// frontend/src/components/crm/LeadDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { leadAPI, usersAPI } from '../../utils/crmApi';
import { ArrowLeftIcon, PencilIcon, CheckCircleIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useDialog } from '../../components/kibo-ui/dialog';
import { useCRM } from '../../contexts/CRMContext';

const LeadDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { branding } = useBranding();
    const { openDialog } = useDialog();
    const { user } = useCRM();
    const [loading, setLoading] = useState(true);
    const [lead, setLead] = useState(null);
    const [showConvertDialog, setShowConvertDialog] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [converting, setConverting] = useState(false);
    const [convertType, setConvertType] = useState('client'); // 'client' or 'account'
    const [clientFormData, setClientFormData] = useState({
        name: '',
        email: '',
        phoneE164: '',
        countryNationality: '',
        countryResidence: '',
        preferredLanguage: 'en',
        timezone: 'UTC',
        status: 'prospect',
        leadSource: 'web_form',
        clientPartnerUserId: '',
        accountId: ''
    });
    const [accountFormData, setAccountFormData] = useState({
        name: '',
        size: '1-5',
        website: '',
        domain: '',
        primaryContact: {
            name: '',
            email: '',
            phoneE164: ''
        },
        address: {
            line1: '',
            city: '',
            country: ''
        },
        ownerUserId: '',
        lifecycle: 'lead',
        segment: 'SMB'
    });

    useEffect(() => {
        loadLead();
    }, [id]);

    const loadLead = async () => {
        try {
            setLoading(true);
            const response = await leadAPI.getById(id);
            if (response.data?.success) {
                setLead(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load lead:', error);
            toast.error('Failed to load lead');
            navigate('/leads');
        } finally {
            setLoading(false);
        }
    };

    const loadUsersAndAccounts = async () => {
        try {
            setUsersLoading(true);
            setAccountsLoading(true);
            
            const { accountAPI } = await import('../../utils/crmApi');
            const [usersRes, accountsRes] = await Promise.all([
                usersAPI.getAll(),
                accountAPI.getAll({ limit: 1000 }).catch(() => ({ data: { success: true, data: [] } }))
            ]);
            
            if (usersRes.data?.success) {
                setUsers(usersRes.data.data || []);
            }
            
            if (accountsRes.data?.success) {
                setAccounts(accountsRes.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load users/accounts:', error);
            toast.error('Failed to load data');
        } finally {
            setUsersLoading(false);
            setAccountsLoading(false);
        }
    };

    const handleConvert = async () => {
        if (!lead) return;
        
        // Pre-fill client form with lead data
        setClientFormData({
            name: `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
            email: lead.email || '',
            phoneE164: lead.phoneE164 || lead.phone || '',
            countryNationality: lead.country || '',
            countryResidence: lead.country || '',
            preferredLanguage: lead.preferredLanguage || 'en',
            timezone: lead.timezone || 'UTC',
            status: 'prospect',
            leadSource: lead.source || 'web_form',
            clientPartnerUserId: lead.assignedTo?._id || lead.assignedTo || user?._id || '',
            accountId: ''
        });
        
        // Pre-fill account form with lead data (but keep name blank for user to enter)
        const emailDomain = lead.email?.split('@')[1]?.toLowerCase();
        setAccountFormData({
            name: '', // Keep blank - user should enter company name
            size: '1-5',
            website: '',
            domain: emailDomain || '',
            primaryContact: {
                name: `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
                email: lead.email || '',
                phoneE164: lead.phoneE164 || lead.phone || ''
            },
            address: {
                line1: '',
                city: lead.city || '',
                country: lead.country || ''
            },
            ownerUserId: lead.assignedTo?._id || lead.assignedTo || user?._id || '',
            lifecycle: 'lead',
            segment: 'SMB'
        });
        
        setConvertType('client'); // Default to client
        setShowConvertDialog(true);
        await loadUsersAndAccounts();
    };

    const handleConvertSubmit = async () => {
        if (!lead) return;
        
        if (convertType === 'account') {
            // Validate account data
            if (!accountFormData.name || !accountFormData.size) {
                toast.error('Account name and size are required');
                return;
            }
            
            if (!accountFormData.ownerUserId) {
                toast.error('Please assign an owner');
                return;
            }
            
            try {
                setConverting(true);
                const response = await leadAPI.convert(lead._id, {
                    convertType: 'account',
                    accountData: {
                        name: accountFormData.name,
                        size: accountFormData.size,
                        website: accountFormData.website || undefined,
                        domain: accountFormData.domain || undefined,
                        primaryContact: accountFormData.primaryContact,
                        address: accountFormData.address,
                        ownerUserId: accountFormData.ownerUserId,
                        lifecycle: accountFormData.lifecycle,
                        segment: accountFormData.segment
                    }
                });
                
                if (response.data?.success) {
                    toast.success('Lead converted to account successfully!');
                    setShowConvertDialog(false);
                    if (response.data.data?.account?._id) {
                        setTimeout(() => {
                            navigate(`/accounts/${response.data.data.account._id}`);
                        }, 500);
                    } else {
                        loadLead();
                    }
                }
            } catch (error) {
                console.error('Failed to convert lead:', error);
                const errorMessage = error.response?.data?.message || 'Failed to convert lead';
                toast.error(errorMessage);
            } finally {
                setConverting(false);
            }
        } else {
            // Validate client data
            if (!clientFormData.name || !clientFormData.email) {
                toast.error('Name and email are required');
                return;
            }
            
            if (!clientFormData.clientPartnerUserId) {
                toast.error('Please assign a partner/user');
                return;
            }
            
            try {
                setConverting(true);
                const response = await leadAPI.convert(lead._id, {
                    convertType: 'client',
                    clientData: {
                        name: clientFormData.name,
                        email: clientFormData.email,
                        phoneE164: clientFormData.phoneE164 || undefined,
                        countryNationality: clientFormData.countryNationality || undefined,
                        countryResidence: clientFormData.countryResidence || undefined,
                        preferredLanguage: clientFormData.preferredLanguage,
                        timezone: clientFormData.timezone,
                        status: clientFormData.status,
                        leadSource: clientFormData.leadSource,
                        clientPartnerUserId: clientFormData.clientPartnerUserId,
                        ...(clientFormData.accountId && { accountId: clientFormData.accountId })
                    }
                });
                
                if (response.data?.success) {
                    toast.success('Lead converted to client successfully!');
                    setShowConvertDialog(false);
                    if (response.data.data?.client?._id) {
                        setTimeout(() => {
                            navigate(`/clients/${response.data.data.client._id}`);
                        }, 500);
                    } else {
                        loadLead();
                    }
                }
            } catch (error) {
                console.error('Failed to convert lead:', error);
                const errorMessage = error.response?.data?.message || 'Failed to convert lead';
                toast.error(errorMessage);
            } finally {
                setConverting(false);
            }
        }
    };

    const handleDelete = () => {
        openDialog({
            title: 'Delete Lead',
            description: 'Are you sure you want to delete this lead? This action cannot be undone.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await leadAPI.delete(id);
                    toast.success('Lead deleted successfully');
                    navigate('/leads');
                } catch (error) {
                    toast.error('Failed to delete lead');
                }
            }
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!lead) {
        return (
            <div className="text-center py-12">
                <p className="text-muted-foreground">Lead not found</p>
            </div>
        );
    }

    const getStatusColor = (status) => {
        const colors = {
            'new': 'bg-blue-100 text-blue-800',
            'contacted': 'bg-yellow-100 text-yellow-800',
            'qualified': 'bg-green-100 text-green-800',
            'nurturing': 'bg-purple-100 text-purple-800',
            'converted': 'bg-emerald-100 text-emerald-800',
            'disqualified': 'bg-gray-100 text-gray-800',
            'lost': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate('/leads')}
                        className="p-2 text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeftIcon className="h-6 w-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">
                            {lead.firstName} {lead.lastName}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">{lead.email}</p>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    {!lead.convertedToClient && (
                        <button
                            onClick={handleConvert}
                            className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white"
                            style={{ backgroundColor: branding.secondaryColor }}
                        >
                            <CheckCircleIcon className="h-5 w-5 mr-2" />
                            Convert to Client
                        </button>
                    )}
                    <Link
                        to={`/leads/${id}/edit`}
                        className="inline-flex items-center px-4 py-2 border border-input rounded-lg text-sm font-medium text-foreground hover:bg-muted"
                    >
                        <PencilIcon className="h-5 w-5 mr-2" />
                        Edit
                    </Link>
                    <button
                        onClick={handleDelete}
                        className="inline-flex items-center px-4 py-2 border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
                    >
                        <TrashIcon className="h-5 w-5 mr-2" />
                        Delete
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Lead Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Status</label>
                                <div className="mt-1">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                                        {lead.status?.charAt(0).toUpperCase() + lead.status?.slice(1) || 'New'}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Score</label>
                                <div className="mt-1 flex items-center">
                                    <span className="text-lg font-semibold text-foreground mr-2">{lead.score || 0}</span>
                                    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                        <div 
                                            className="h-full rounded-full transition-all"
                                            style={{ 
                                                width: `${lead.score || 0}%`,
                                                backgroundColor: lead.score >= 70 ? '#10B981' : lead.score >= 40 ? '#F59E0B' : '#6B7280'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Source</label>
                                <p className="mt-1 text-sm text-foreground capitalize">
                                    {lead.source?.replace('_', ' ') || '—'}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Company</label>
                                <p className="mt-1 text-sm text-foreground">{lead.company || '—'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Phone</label>
                                <p className="mt-1 text-sm text-foreground">{lead.phone || '—'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Job Title</label>
                                <p className="mt-1 text-sm text-foreground">{lead.jobTitle || '—'}</p>
                            </div>
                        </div>
                    </div>

                    {lead.convertedToClient && lead.convertedClientId && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
                            <div className="flex items-center">
                                <CheckCircleIcon className="h-6 w-6 text-emerald-600 mr-3" />
                                <div>
                                    <h3 className="text-lg font-semibold text-emerald-900">Lead Converted</h3>
                                    <p className="text-sm text-emerald-700 mt-1">
                                        This lead was converted to a client on {new Date(lead.convertedAt).toLocaleDateString()}
                                    </p>
                                    <Link
                                        to={`/clients/${lead.convertedClientId._id || lead.convertedClientId}`}
                                        className="text-sm font-medium text-emerald-700 hover:text-emerald-900 mt-2 inline-block"
                                    >
                                        View Client →
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="space-y-6">
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Engagement</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Email Opens</label>
                                <p className="text-sm text-foreground">{lead.engagement?.emailOpens || 0}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Email Clicks</label>
                                <p className="text-sm text-foreground">{lead.engagement?.emailClicks || 0}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Form Submissions</label>
                                <p className="text-sm text-foreground">{lead.engagement?.formSubmissions || 0}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">Website Visits</label>
                                <p className="text-sm text-foreground">{lead.engagement?.websiteVisits || 0}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Convert Lead Dialog */}
            {showConvertDialog && lead && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[9999] p-4 overflow-y-auto">
                    <div className="bg-card rounded-lg shadow-xl border border-border max-w-2xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col">
                        <div className="p-6 border-b border-border flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-foreground">Convert Lead to {convertType === 'account' ? 'Account' : 'Client'}</h2>
                                <button
                                    onClick={() => {
                                        setShowConvertDialog(false);
                                    }}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                                Review and complete the {convertType === 'account' ? 'account' : 'client'} information. Fields marked with * are required.
                            </p>
                        </div>

                        <div className="p-6 space-y-6 overflow-y-auto flex-1">
                            {/* Conversion Type Selector */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Convert To <span className="text-red-500">*</span>
                                </label>
                                <div className="flex space-x-4">
                                    <button
                                        type="button"
                                        onClick={() => setConvertType('client')}
                                        className={`flex-1 px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                                            convertType === 'client'
                                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                : 'border-input bg-background text-foreground hover:bg-muted'
                                        }`}
                                    >
                                        <div className="font-semibold">Client</div>
                                        <div className="text-xs mt-1">Individual person or contact</div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setConvertType('account')}
                                        className={`flex-1 px-4 py-3 border-2 rounded-lg text-sm font-medium transition-colors ${
                                            convertType === 'account'
                                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                : 'border-input bg-background text-foreground hover:bg-muted'
                                        }`}
                                    >
                                        <div className="font-semibold">Account</div>
                                        <div className="text-xs mt-1">Company or organization</div>
                                    </button>
                                </div>
                            </div>

                            {convertType === 'client' ? (
                                <>
                                    {/* Client Form - Basic Information */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={clientFormData.name}
                                                    onChange={(e) => setClientFormData(prev => ({ ...prev, name: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Email <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    value={clientFormData.email}
                                                    onChange={(e) => setClientFormData(prev => ({ ...prev, email: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Phone
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={clientFormData.phoneE164}
                                                    onChange={(e) => setClientFormData(prev => ({ ...prev, phoneE164: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="+1234567890"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Status
                                                </label>
                                                <select
                                                    value={clientFormData.status}
                                                    onChange={(e) => setClientFormData(prev => ({ ...prev, status: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="prospect">Prospect</option>
                                                    <option value="active">Active</option>
                                                    <option value="paused">Paused</option>
                                                    <option value="churned">Churned</option>
                                                    <option value="closed_lost">Closed Lost</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Location Information */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Location Information</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Country (Nationality)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={clientFormData.countryNationality}
                                                    onChange={(e) => setClientFormData(prev => ({ ...prev, countryNationality: e.target.value.toUpperCase() }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="ES"
                                                    maxLength={2}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Country (Residence)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={clientFormData.countryResidence}
                                                    onChange={(e) => setClientFormData(prev => ({ ...prev, countryResidence: e.target.value.toUpperCase() }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="ES"
                                                    maxLength={2}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Preferred Language
                                                </label>
                                                <select
                                                    value={clientFormData.preferredLanguage}
                                                    onChange={(e) => setClientFormData(prev => ({ ...prev, preferredLanguage: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="en">English</option>
                                                    <option value="es">Spanish</option>
                                                    <option value="fr">French</option>
                                                    <option value="de">German</option>
                                                    <option value="it">Italian</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Timezone
                                                </label>
                                                <input
                                                    type="text"
                                                    value={clientFormData.timezone}
                                                    onChange={(e) => setClientFormData(prev => ({ ...prev, timezone: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="Europe/Madrid"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Assignment */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Assignment</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Assign To <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={clientFormData.clientPartnerUserId}
                                                    onChange={(e) => setClientFormData(prev => ({ ...prev, clientPartnerUserId: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    required
                                                >
                                                    <option value="">Select a user...</option>
                                                    {usersLoading ? (
                                                        <option disabled>Loading users...</option>
                                                    ) : users.length === 0 ? (
                                                        <option disabled>No users available</option>
                                                    ) : (
                                                        users.map(u => (
                                                            <option key={u._id} value={u._id}>
                                                                {u.name || u.email}
                                                            </option>
                                                        ))
                                                    )}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Account (Optional)
                                                </label>
                                                <select
                                                    value={clientFormData.accountId}
                                                    onChange={(e) => setClientFormData(prev => ({ ...prev, accountId: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="">No account</option>
                                                    {accountsLoading ? (
                                                        <option disabled>Loading accounts...</option>
                                                    ) : accounts.length === 0 ? (
                                                        <option disabled>No accounts available</option>
                                                    ) : (
                                                        accounts.map(account => (
                                                            <option key={account._id} value={account._id}>
                                                                {account.name || account.companyName}
                                                            </option>
                                                        ))
                                                    )}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Lead Source
                                                </label>
                                                <select
                                                    value={clientFormData.leadSource}
                                                    onChange={(e) => setClientFormData(prev => ({ ...prev, leadSource: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="web_form">Web Form</option>
                                                    <option value="referral">Referral</option>
                                                    <option value="social_media">Social Media</option>
                                                    <option value="email_campaign">Email Campaign</option>
                                                    <option value="phone_call">Phone Call</option>
                                                    <option value="walk_in">Walk In</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Account Form */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Account Information</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Account Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={accountFormData.name}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, name: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Company Size <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={accountFormData.size}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, size: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    required
                                                >
                                                    <option value="1-5">1-5 employees</option>
                                                    <option value="5-10">5-10 employees</option>
                                                    <option value="10-50">10-50 employees</option>
                                                    <option value="50-100">50-100 employees</option>
                                                    <option value="100-1000">100-1000 employees</option>
                                                    <option value="1000+">1000+ employees</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Website
                                                </label>
                                                <input
                                                    type="url"
                                                    value={accountFormData.website}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, website: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="https://example.com"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Domain
                                                </label>
                                                <input
                                                    type="text"
                                                    value={accountFormData.domain}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, domain: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="example.com"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Primary Contact</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Contact Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={accountFormData.primaryContact.name}
                                                    onChange={(e) => setAccountFormData(prev => ({
                                                        ...prev,
                                                        primaryContact: { ...prev.primaryContact, name: e.target.value }
                                                    }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Contact Email
                                                </label>
                                                <input
                                                    type="email"
                                                    value={accountFormData.primaryContact.email}
                                                    onChange={(e) => setAccountFormData(prev => ({
                                                        ...prev,
                                                        primaryContact: { ...prev.primaryContact, email: e.target.value }
                                                    }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Contact Phone
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={accountFormData.primaryContact.phoneE164}
                                                    onChange={(e) => setAccountFormData(prev => ({
                                                        ...prev,
                                                        primaryContact: { ...prev.primaryContact, phoneE164: e.target.value }
                                                    }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="+1234567890"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold text-foreground mb-4">Assignment</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Owner <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={accountFormData.ownerUserId}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, ownerUserId: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    required
                                                >
                                                    <option value="">Select a user...</option>
                                                    {usersLoading ? (
                                                        <option disabled>Loading users...</option>
                                                    ) : users.length === 0 ? (
                                                        <option disabled>No users available</option>
                                                    ) : (
                                                        users.map(u => (
                                                            <option key={u._id} value={u._id}>
                                                                {u.name || u.email}
                                                            </option>
                                                        ))
                                                    )}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Lifecycle
                                                </label>
                                                <select
                                                    value={accountFormData.lifecycle}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, lifecycle: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="lead">Lead</option>
                                                    <option value="prospect">Prospect</option>
                                                    <option value="customer">Customer</option>
                                                    <option value="churned">Churned</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Segment
                                                </label>
                                                <select
                                                    value={accountFormData.segment}
                                                    onChange={(e) => setAccountFormData(prev => ({ ...prev, segment: e.target.value }))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="SMB">SMB</option>
                                                    <option value="Agency">Agency</option>
                                                    <option value="Corp">Corp</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="p-6 border-t border-border flex items-center justify-end space-x-3 flex-shrink-0">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowConvertDialog(false);
                                }}
                                className="px-4 py-2 border border-input rounded-md text-sm font-medium text-foreground hover:bg-muted"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConvertSubmit}
                                disabled={converting}
                                className="px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: branding.primaryColor }}
                            >
                                {converting ? 'Converting...' : convertType === 'account' ? 'Convert to Account' : 'Convert to Client'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeadDetail;


